import { useAuthContext } from "@/contexts/AuthContext"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { AvatarUpload } from "@/components/ui/avatar-upload"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useNavigate } from "react-router-dom"
import { Edit, Mail, Phone, User, Store, InfoIcon, Cake, CalendarCog } from "lucide-react"
import Layout from "@/components/Layout/Layout"
import { CountryFlag } from "@/components/ui/country-flag"
import { useCountries } from "@/graphql"

const randomPlaceholderLogo = (id: number) => {
  const placeholders = [1, 2, 3, 4, 5] // tus imágenes disponibles
  const lastDigit = id % 10 // obtiene el último dígito
  const index = placeholders[lastDigit % placeholders.length]
  return `/img/placeholder-logo-tienda${index}.png`
}

/**
 * Vista del perfil de usuario.
 */
const Profile = () => {
  const { userInfo, loading, isAuthenticated } = useAuthContext()
  const navigate = useNavigate()
  const { countriesMap } = useCountries()

  if (loading) {
    return <LoadingScreen message="Cargando perfil..." />
  }
  if (!isAuthenticated) {
    navigate("/login")
    return null
  }

  const person = userInfo?.User?.Person

  if (!person) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No se encontró información del usuario</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <Card className="p-4 sm:p-6 bg-card border border-border dark:bg-gray-800 dark:border-gray-700 transition-colors duration-300">
          <CardHeader className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-gray-700 dark:text-gray-200" />
              <CardTitle className="text-gray-900 dark:text-gray-100">
                Información Personal
              </CardTitle>
            </div>
            <Button
              onClick={() => navigate("/profile/edit")}
              className="w-full sm:w-auto flex-none"
            >
              <Edit className="mr-2 h-4 w-4" />
              Editar Perfil
            </Button>
          </CardHeader>

          <CardContent className="flex flex-col sm:flex-row sm:items-start gap-6 mt-2">
            {/* Avatar */}
            <div className="flex flex-col items-center gap-2 sm:items-start shrink-0">
              <AvatarUpload
                src={person.urlAvatar}
                name={person.fullName}
                personId={person.id}
                type="user.avatar"
              />
              <div className="text-center sm:text-left">
                <p className="font-semibold text-foreground leading-tight">{person.fullName}</p>
                <p className="text-xs text-muted-foreground">#{person.code}</p>
              </div>
            </div>

            {/* Separador vertical en desktop */}
            <div className="hidden sm:block w-px bg-border self-stretch" />

            {/* Campos de información */}
            <div className="flex-1 min-w-0">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {/* Email */}
                <div className="flex items-start gap-2 min-w-0">
                  <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <dt className="text-xs text-muted-foreground">Correo</dt>
                    <dd className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-foreground truncate">{person.email}</span>
                      {person.emailValid === 1 ? (
                        <Badge variant="default" className="text-[10px] px-1.5 py-0">
                          Verificado
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                          No verificado
                        </Badge>
                      )}
                    </dd>
                  </div>
                </div>

                {/* Teléfono */}
                {person.phone && (
                  <div className="flex items-start gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <dt className="text-xs text-muted-foreground">Teléfono</dt>
                      <dd className="text-foreground">{person.phone}</dd>
                    </div>
                  </div>
                )}

                {/* Género */}
                {person.Gender && (
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <dt className="text-xs text-muted-foreground">Género</dt>
                      <dd className="text-foreground">{person.Gender.name}</dd>
                    </div>
                  </div>
                )}

                {/* Fecha de nacimiento */}
                {person.birthday && (
                  <div className="flex items-start gap-2">
                    <Cake className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <dt className="text-xs text-muted-foreground">Nacimiento</dt>
                      <dd className="text-foreground">
                        {new Date(person.birthday).toLocaleDateString("es-ES", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </dd>
                    </div>
                  </div>
                )}

                {/* Fecha de registro */}
                <div className="flex items-start gap-2">
                  <CalendarCog className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <dt className="text-xs text-muted-foreground">Miembro desde</dt>
                    <dd className="text-foreground">
                      {new Date(person.created).toLocaleDateString("es-ES", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </dd>
                  </div>
                </div>

                {/* Estado */}
                <div className="flex items-start gap-2">
                  <InfoIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <dt className="text-xs text-muted-foreground">Estado</dt>
                    <dd>
                      <Badge
                        variant={person.active ? "default" : "secondary"}
                        className="text-[10px] px-1.5 py-0"
                      >
                        {person.active ? "Activo" : "Inactivo · Baneado"}
                      </Badge>
                    </dd>
                  </div>
                </div>

                {/* Dirección */}
                <div className="flex items-start gap-2 sm:col-span-2">
                  <div className="mt-0.5 shrink-0">
                    <CountryFlag countryCode={countriesMap.get(person.Address.countryId).iso} />
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Ubicación</dt>
                    <dd className="text-foreground">
                      {[person.Address.Location?.name, person.Address.Region?.fullName]
                        .filter(Boolean)
                        .join(", ")}
                    </dd>
                  </div>
                </div>
              </dl>
            </div>
          </CardContent>
        </Card>

        {/* Store */}
        {person?.Store && (
          <Card className="bg-card border border-border dark:bg-gray-800 dark:border-gray-700 transition-colors duration-300">
            <CardHeader className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <Store className="w-5 h-5 text-primary dark:text-primary/80" />
                <CardTitle className="text-gray-900 dark:text-gray-100">Mi Tienda</CardTitle>
              </div>
              <Button
                variant="default"
                onClick={() => navigate(`/stores/${person.Store.id}/edit`)}
                className="w-full sm:w-auto"
              >
                <Edit className="mr-2 w-4 h-4" />
                Editar Tienda
              </Button>
            </CardHeader>

            <CardContent className="flex flex-col sm:flex-row gap-4">
              {/* Avatar/Logo */}
              <AvatarUpload
                src={person.Store.urlLogo || randomPlaceholderLogo(person.Store.id)}
                name={person.Store.name || "Tienda"}
                personId={person.Store.id || 0}
                type="store.logo"
              />

              {/* Información */}
              <div className="flex-1 flex flex-col gap-2 text-center sm:text-left text-gray-700 dark:text-gray-300">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <Store className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {person.Store.name}
                  </span>
                </div>

                {person.Store.description && (
                  <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                    {person.Store.description}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 items-center justify-center sm:justify-start">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span>{person.Store.email}</span>
                  </div>
                  {person.Store.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span>{person.Store.phone}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    onClick={() => navigate(person.Store.urlView)}
                  >
                    Ver Tienda
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    onClick={() => navigate(person.Store.urlTournaments)}
                  >
                    Mis Torneos
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  )
}

export default Profile
